import React from 'react';
import { motion } from 'framer-motion';
import { FileText, Image, Search } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { SuggestedTask } from '@/hooks/goal-planner/suggested-task-types';
import { detectContentTypes, getContentTypeLabel, type ContentType } from '@/utils/task-content-detection';

interface TaskItemCardProps {
  task: SuggestedTask;
  isSelected: boolean;
  onToggleSelection: (taskId: string) => void;
  index?: number;
}

export const TaskItemCard: React.FC<TaskItemCardProps> = ({
  task,
  isSelected,
  onToggleSelection,
  index = 0
}) => {
  const displayDescription = task.description || 'No description available';
  const truncatedDescription = displayDescription.length > 120 
    ? displayDescription.substring(0, 120) + '...'
    : displayDescription;

  // Detect content generation opportunities
  const contentDetection = detectContentTypes(task.title, task.description || '');

  const getIconForContentType = (type: ContentType) => {
    switch (type) {
      case 'document':
        return FileText;
      case 'image':
        return Image;
      case 'research':
        return Search;
      default:
        return FileText;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ delay: index * 0.02 }}
      className={cn(
        "group flex items-start gap-3 p-3 rounded-lg border transition-all duration-200 cursor-pointer mb-2",
        isSelected
          ? "bg-amber-50/70 dark:bg-amber-900/20 border-amber-200 dark:border-amber-600"
          : "bg-gray-50/50 dark:bg-gray-800/30 border-gray-200/50 dark:border-gray-700/50 hover:border-amber-200 dark:hover:border-amber-600"
      )}
      onClick={() => onToggleSelection(task.id)}
    >
      {/* Checkbox */}
      <Checkbox
        checked={isSelected}
        onChange={() => onToggleSelection(task.id)}
        variant="blue"
        className="flex-shrink-0 mt-1 transition-all duration-200 group-hover:scale-110 group-hover:ring-2 group-hover:ring-blue-400/50 group-hover:ring-offset-1"
      />
      
      {/* Task Content */}
      <div className="flex-1 min-w-0">
        <h4 className={cn(
          "text-sm font-medium leading-snug mb-1",
          isSelected ? "text-gray-900 dark:text-gray-100" : "text-gray-700 dark:text-gray-300"
        )}>
          {task.title}
        </h4>
        
        <p className={cn(
          "text-xs leading-relaxed mb-2",
          isSelected ? "text-gray-700 dark:text-gray-300" : "text-gray-600 dark:text-gray-400"
        )}>
          {truncatedDescription}
        </p>
        
        {/* Smart Content Generation Tags (Informational) */}
        {contentDetection.detectedTypes.length > 0 && (
          <div className="hidden sm:flex flex-wrap items-start gap-1 mt-1">
            {contentDetection.detectedTypes.map((type) => {
              const IconComponent = getIconForContentType(type);
              return (
                <div
                  key={type}
                  className="flex items-center gap-1 h-5 px-2 text-xs bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400 rounded-full border border-blue-200 dark:border-blue-700"
                >
                  <IconComponent className="h-3 w-3" />
                  <span>{getContentTypeLabel(type)}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </motion.div>
  );
}; 